import React from 'react';
import { Box, Divider, Grid, PropTypes, makeStyles } from '@material-ui/core';
import { formatCurrency, formatDate } from '@utils/formatter';
import {
  CapaRfqQuotationType,
  CapaQuotationServiceType,
  CapaQuotationDetailedQuotation
} from '@generated/graphql';
import { colorSet } from '@components/Provider';
import {
  CustomizedButton,
  CustomizedTypography,
  Stack
} from '@components/Customized';
import WorkScopes from './WorkScopes';
import { useLocation } from 'react-router-dom';
import useFiles from '@hooks/useFiles';
import { Download } from '@carbon/icons-react';

interface Props extends Partial<CapaQuotationDetailedQuotation> {
  matches?: boolean;
  haveConsulted: boolean;
}

const ReceivedQuotationDesignForm = (props: Props) => {
  const classes = useStyles();
  const { onDownloadPdf } = useFiles();
  const { search } = useLocation();
  const isCreatePurchaseOrderPage = search.includes('create_purchaseorder');

  const supplyCost =
    props?.type === CapaRfqQuotationType.Simple
      ? props.quotation
      : props?.items?.map((v) => v.cost).reduce((prev, curr) => prev + curr, 0);

  const vat = supplyCost ? Math.floor(supplyCost * 0.1) : 0;
  const deliveryCost = props?.deliveryCost ? props.deliveryCost : 0;
  const total = deliveryCost + vat + (supplyCost ? supplyCost : 0);

  const isPcb = props.serviceType === CapaQuotationServiceType.Pcb;

  const selectedOptionalServices = (props?.optionalServices ?? []).map(
    ({ serviceType }) => serviceType
  ) as CapaQuotationServiceType[];

  return (
    <>
      {!isPcb && (
        <>
          <Box style={{ paddingTop: 16 }}>
            <CustomizedTypography
              variant="h4"
              fontWeight="500"
              gutterBottoms={16}
              color="textPrimary"
            >
              작업 범위
            </CustomizedTypography>
            <WorkScopes selectedOptionalServices={selectedOptionalServices} />
          </Box>

          <Divider className="divider" />
        </>
      )}

      <Box>
        <Stack alignItems="center" justifyContent="space-between">
          <CustomizedTypography
            variant="h4"
            fontWeight="500"
            gutterBottoms={16}
            color="textPrimary"
          >
            견적 정보
          </CustomizedTypography>
          {!isCreatePurchaseOrderPage && (
            <CustomizedButton
              style={{
                paddingRight: 0
              }}
              variant="text"
              color="dark"
              startIcon={<Download color={colorSet.gray900} size={20} />}
              onClick={() => {
                onDownloadPdf(
                  'quotation',
                  props?.id ?? '',
                  props?.projectName ?? ''
                );
              }}
            >
              견적서 다운로드
            </CustomizedButton>
          )}
        </Stack>

        <Box>
          <Grid container className={classes.tableHeader} wrap="nowrap">
            {[
              { title: '항목', width: '448px', align: 'left' },
              { title: '금액', width: '152px', align: 'right' }
            ].map((v, i) => (
              <Grid item key={i}>
                <CustomizedTypography
                  align={v.align as PropTypes.Alignment}
                  variant="subtitle2"
                  color="textSecondary"
                >
                  {v.title}
                </CustomizedTypography>
              </Grid>
            ))}
          </Grid>
        </Box>
        {props?.items?.map((v, i) => (
          <Grid container key={i} className={classes.tableData} wrap="nowrap">
            <Grid item>
              <CustomizedTypography
                variant="body2"
                color="textSecondary"
                className="index"
              >
                {i + 1}
              </CustomizedTypography>
              <Box marginLeft={'4px'} className="fileName">
                <CustomizedTypography
                  variant="body1"
                  color="textPrimary"
                  noWrap
                >
                  {v.fileName}
                </CustomizedTypography>
              </Box>
            </Grid>

            <Grid className={'price'} item>
              <CustomizedTypography
                variant="body1"
                fontWeight="500"
                color="textPrimary"
              >
                {formatCurrency(Number(v.cost)) + ' 원'}
              </CustomizedTypography>
            </Grid>
          </Grid>
        ))}
      </Box>
      {props.matches && <Divider className="divider" />}

      <Box>
        <Grid
          container
          wrap="nowrap"
          direction="column"
          className={classes.priceInfo}
        >
          <Grid item xs={12} className="price-item gutter-bottom-8">
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              공급가액
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="500"
            >
              {formatCurrency(supplyCost ?? 0)}&nbsp;원
            </CustomizedTypography>
          </Grid>
          <Grid item xs={12} className="price-item gutter-bottom-8">
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              부가세 (10%)
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="500"
            >
              {supplyCost ? formatCurrency(vat) : 0}
              &nbsp;원
            </CustomizedTypography>
          </Grid>
          <Grid item xs={12} className="price-item">
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              배송비
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="500"
            >
              {deliveryCost ? formatCurrency(deliveryCost) : '0'}
              &nbsp;원
            </CustomizedTypography>
          </Grid>
        </Grid>
        <Divider style={{ margin: '16px 0', background: colorSet.gray1000 }} />

        <Grid
          container
          wrap="nowrap"
          direction="column"
          className={classes.priceInfo}
        >
          <Grid item xs={12} className="price-item">
            <CustomizedTypography
              variant="h4"
              color="textPrimary"
              fontWeight="500"
            >
              총 견적금액
            </CustomizedTypography>
            <CustomizedTypography variant="h4" color="primary" fontWeight="500">
              {formatCurrency(total ?? 0)}&nbsp;원
            </CustomizedTypography>
          </Grid>
        </Grid>
      </Box>

      <Divider className="divider" />

      {props?.etc && (
        <React.Fragment>
          <Box className={classes.explanation}>
            <CustomizedTypography
              variant="h4"
              fontWeight="500"
              gutterBottoms={16}
              color="textPrimary"
            >
              견적 설명
            </CustomizedTypography>

            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
              className="pre-line"
            >
              {props?.etc}
            </CustomizedTypography>
          </Box>
          <Divider className="divider" />
        </React.Fragment>
      )}

      <Box className={classes.explanation}>
        <CustomizedTypography
          variant="h4"
          fontWeight="500"
          gutterBottoms={16}
          color="textPrimary"
        >
          시안 조율 조건
        </CustomizedTypography>

        <Grid
          container
          direction="column"
          wrap="nowrap"
          className={classes.draftInfo}
        >
          <Grid item xs={12} className="maxDraftCount">
            <CustomizedTypography
              variant="body1"
              color="primary"
              fontWeight="500"
            >
              최대 {props?.maxDraftModificationCount}회까지 수정 가능
            </CustomizedTypography>
          </Grid>
          {props?.draftModificationTerms && (
            <Grid item xs={12} className="draftTerms">
              <CustomizedTypography
                variant="body1"
                color="textPrimary"
                className="pre-line"
              >
                {props?.draftModificationTerms}
              </CustomizedTypography>
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider className="divider" />

      {isNaN(Number(props?.dueDate)) || Number(props?.dueDate) === 0 ? null : (
        <React.Fragment>
          <Box>
            <CustomizedTypography
              variant="h4"
              fontWeight="500"
              gutterBottoms={16}
              color="textPrimary"
            >
              납품 정보
            </CustomizedTypography>

            <Grid container direction="column" className={classes.grid}>
              <Grid className="item gutter-bottom-8" item xs={12}>
                <CustomizedTypography
                  className="title"
                  variant="body1"
                  color="textSecondary"
                  fontWeight="normal"
                >
                  납품 가능일
                </CustomizedTypography>
                <CustomizedTypography
                  variant="body1"
                  color="primary"
                  fontWeight="500"
                >
                  발주일로부터 {props?.dueDate}일
                </CustomizedTypography>
              </Grid>
            </Grid>
          </Box>
        </React.Fragment>
      )}

      <Divider className="divider" />

      <Box>
        <CustomizedTypography
          variant="h4"
          fontWeight="500"
          gutterBottoms={16}
          color="textPrimary"
        >
          발행 정보
        </CustomizedTypography>

        <Grid container direction="column" className={classes.grid}>
          <Grid className="item gutter-bottom-8" item xs={12}>
            <CustomizedTypography
              className="title"
              variant="body1"
              color="textSecondary"
              fontWeight="normal"
            >
              수신처
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              {props?.publish?.organizationName}
            </CustomizedTypography>
          </Grid>
          <Grid className="item gutter-bottom-8" item xs={12}>
            <CustomizedTypography
              className="title"
              variant="body1"
              color="textSecondary"
              fontWeight="normal"
            >
              프로젝트명
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              {props?.projectName}
            </CustomizedTypography>
          </Grid>
          <Grid className="item gutter-bottom-8" item xs={12}>
            <CustomizedTypography
              className="title"
              variant="body1"
              color="textSecondary"
              fontWeight="normal"
            >
              발행일시
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              {formatDate(
                props?.createdAt as string,
                'YYYY년 MM월 DD일 HH시 mm분'
              )}
            </CustomizedTypography>
          </Grid>
          {isNaN(Number(props?.expiration)) ||
          !props?.expiration ||
          Number(props.expiration) === 0 ? null : (
            <Grid className="item" item xs={12}>
              <CustomizedTypography
                className="title"
                variant="body1"
                color="textSecondary"
                fontWeight="normal"
              >
                유효기간
              </CustomizedTypography>
              <CustomizedTypography
                variant="body1"
                color="textPrimary"
                fontWeight="normal"
              >
                발행일로부터 {props?.expiration}일
              </CustomizedTypography>
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider className="divider" />

      <Box>
        <CustomizedTypography
          variant="h4"
          fontWeight="500"
          gutterBottoms={16}
          color="textPrimary"
        >
          공급자 정보
        </CustomizedTypography>

        <Grid container direction="column" className={classes.grid}>
          <Grid className="item gutter-bottom-8" item xs={12}>
            <CustomizedTypography
              className="title"
              variant="body1"
              color="textSecondary"
              fontWeight="normal"
            >
              상호
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              {props?.supply?.organizationName}
            </CustomizedTypography>
          </Grid>
          <Grid className="item gutter-bottom-8" item xs={12}>
            <CustomizedTypography
              className="title"
              variant="body1"
              color="textSecondary"
              fontWeight="normal"
            >
              등록번호
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="normal"
            >
              {props?.supply?.companyRegistrationNumber}
            </CustomizedTypography>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default ReceivedQuotationDesignForm;

const useStyles = makeStyles((theme) => ({
  contents: {
    width: '100%',
    height: '100%',
    maxWidth: 600,
    margin: '0px auto',
    padding: '32px 16px',
    '& .pre-line': {
      whiteSpace: 'pre-line'
    },
    [theme.breakpoints.down('md')]: {
      padding: '32px 0px',
      maxWidth: '100%'
    }
  },

  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${colorSet.gray200}`,
    fontSize: 16,
    '& > div': {
      padding: '8px 0px'
    }
  },
  tableData: {
    justifyContent: 'space-between',
    height: '100%',
    padding: '21px 0px',
    borderBottom: `1px solid ${colorSet.gray200}`,
    '& > :nth-child(1)': {
      display: 'flex',
      alignItems: 'center',
      maxWidth: 'calc(100% - 80px)',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'pre',
      '& .index': {
        width: 14
      },
      '& .thumbnail': {
        border: `1px solid ${colorSet.gray200}`,
        maxWidth: 48,
        width: '100%',
        height: '48px',
        borderRadius: 8
      },
      '& .fileName': {
        maxWidth: 450,
        [theme.breakpoints.down('md')]: {
          maxWidth: '100%'
        }
      }
    },
    '& > :nth-child(2)': {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      flexDirection: 'column',
      maxWidth: 80,
      '& > p': {
        maxWidth: '100%',
        textAlign: 'right',
        wordBreak: 'break-all'
      }
    }
  },
  grid: {
    '& .item': {
      display: 'flex',
      alignItems: 'center',
      '& .title': {
        width: 85,
        marginRight: 8
      },
      '&.gutter-bottom-8': {
        marginBottom: 8
      }
    }
  },
  priceInfo: {
    marginTop: 27,
    '& .price-item': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '&.gutter-bottom-8': {
        marginBottom: 8
      }
    }
  },

  draftInfo: {
    '& .draftInfo-item': {
      width: 65,
      wordBreak: 'break-all',
      marginRight: 8,
      maxHeight: 64
    },
    '& .maxDraftCount': {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 8,
      '& > :last-child': {
        wordBreak: 'break-all',
        maxWidth: 495
      }
    },
    '& .draftTerms': {
      display: 'flex',
      alignItems: 'flex-start',
      '& > :last-child': {
        wordBreak: 'break-all',
        [theme.breakpoints.down('md')]: {
          maxWidth: '100%'
        }
      }
    }
  },
  withdrawal: {
    display: 'flex',
    width: '100%',
    backgroundColor: colorSet.errorBackGround,
    borderRadius: 8,
    padding: 13,
    '& svg': {
      width: 20,
      height: 20,
      marginRight: 8,
      color: colorSet.errorMain
    }
  },
  explanation: {
    '& .pre-line': {
      whiteSpace: 'pre-line',
      wordBreak: 'break-all'
    }
  }
}));
